import React from "react";
import type { ApolloClient } from "@apollo/client";
import { connect, type ConnectedProps } from "react-redux";
import { withApollo, type WithApolloClient } from "@apollo/client/react/hoc";
import { getContributionAbilityAndEstimates } from "actions/contributionActions";
import { Row, Col } from "react-bootstrap";
import { isEmpty } from "lodash";

import { getAllAccounts } from "actions/accountActions";
import {
  activeAccountType,
  hasPaychexEmployeeWorkerRecords,
  userIsPaychexPerks,
} from "store/selectors/user";
import { getLinkedEmployersSelector } from "store/selectors/employerLink";
import { accountTypesToEnglishMapping } from "statics/accountTypes";
import { push } from "store/actions/router";
import {
  getLinkedEmployers,
  getEmployerLinkRequests,
  getPaychexWorkerRecords,
} from "actions/userActions";
import AnnualContributionsWidget from "./AnnualContributionsWidget";
import IconSpinner from "components/IconSpinner";
import Employers from "./Employers";
import Banks from "./Banks";
import PaycheckSplitCard from "./PaycheckSplitCard";
import { getIraAccountIdSelector } from "store/selectors/user";

import "./IndividualContributions.scss";
import { activeInvestmentAccountsSelector } from "store/selectors/accounts";
import type { RootState } from "types/state";

const mapStateToProps = (state: RootState) => {
  return {
    accountType: activeAccountType(state),
    linkedEmployers: getLinkedEmployersSelector(state),
    accountId: getIraAccountIdSelector(state),
    accounts: activeInvestmentAccountsSelector(state),
    isPaychexPerks: userIsPaychexPerks(state),
    hasPaychexEmployeeWorkerRecords: hasPaychexEmployeeWorkerRecords(state),
  };
};

const mapDispatchToProps = {
  push,
  getLinkedEmployers,
  getEmployerLinkRequests,
  getAllAccounts,
  getContributionAbilityAndEstimates,
  getPaychexWorkerRecords,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

interface OwnProps {
  currentContributionAbility?: React.ComponentProps<
    typeof AnnualContributionsWidget
  >["currentContributionAbility"];
}

type Props = WithApolloClient<OwnProps & PropsFromRedux>;

interface State {
  initialFetching: boolean;
}

class IndividualContributions extends React.PureComponent<Props, State> {
  constructor(props: Props) {
    super(props);

    this.state = {
      initialFetching: true,
    };
  }

  componentDidMount() {
    if (isEmpty(this.props.accountId)) {
      this.props.push("/dashboard?noAccount=true");
      return;
    }
    void Promise.all([
      this.props.getLinkedEmployers(this.props.client as ApolloClient<object>),
      this.props.getEmployerLinkRequests(
        this.props.client as ApolloClient<object>
      ),
      this.props.getContributionAbilityAndEstimates(
        this.props.client as ApolloClient<object>
      ),
      // Re-read every visit so someone who later picks up a split-eligible
      // worker record sees the paycheck-split card appear.
      this.props.isPaychexPerks
        ? this.props.getPaychexWorkerRecords(
            this.props.client as ApolloClient<object>
          )
        : Promise.resolve(),
    ]).then(() => this.setState({ initialFetching: false }));
  }

  _getContent() {
    // Paychex Flex (Perks) individuals have no employer relationship — their
    // primary contributions are payroll deductions managed as PAYCHECK_SPLITs
    // keyed off their worker records, so they get the PaycheckSplitCard in place
    // of the standard Employers section. They can additionally link a bank as a
    // secondary contribution source, so the Banks section is still shown.
    if (this.props.isPaychexPerks) {
      return (
        <>
          {this.props.hasPaychexEmployeeWorkerRecords && <PaycheckSplitCard />}
          <Banks />
          <Row>
            <Col md={12}>
              <AnnualContributionsWidget
                currentContributionAbility={
                  this.props.currentContributionAbility
                }
              />
            </Col>
          </Row>
        </>
      );
    }
    return (
      <>
        <Employers
          // Boundary casts: getLinkedEmployersSelector returns the narrow
          // EmployerLinkLike[] stub (company.id + status only) while Employers
          // reads the fuller Employer shape; activeInvestmentAccountsSelector
          // returns IraAccount[] while Employers wants its own narrow Account[].
          // These widen/narrow across the shallow-typed redux selectors and are
          // load-bearing — values are passed through unchanged at runtime.
          linkedEmployers={
            this.props.linkedEmployers as React.ComponentProps<
              typeof Employers
            >["linkedEmployers"]
          }
          accountId={this.props.accountId}
          accounts={
            this.props.accounts as React.ComponentProps<
              typeof Employers
            >["accounts"]
          }
        />
        <Banks />
        <Row>
          <Col md={12}>
            <AnnualContributionsWidget
              currentContributionAbility={this.props.currentContributionAbility}
            />
          </Col>
        </Row>
      </>
    );
    // todo add bank section
  }

  render() {
    let content;
    if (this.state.initialFetching) {
      content = <IconSpinner centered />;
    } else {
      content = this._getContent();
    }

    return (
      <div className="mega-container" id="user-contributions">
        <section className="page-title-wrap">
          <article className="text-cell">
            <h1 className="page-title">
              {
                accountTypesToEnglishMapping[
                  this.props
                    .accountType as keyof typeof accountTypesToEnglishMapping
                ]
              }{" "}
              Contributions
            </h1>
            <p className="page-subtext" style={{ paddingBottom: 35 }}>
              Contributions overview will help you better manage and grow your
              wealth over time.
            </p>
            <div className="main-content">{content}</div>
          </article>
        </section>
      </div>
    );
  }
}

export default connector(withApollo<Props>(IndividualContributions));
